// App root — configurazione router React Router v6
import { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AppLayout } from './components/layout/AppLayout'
import { ProtectedRoute } from './components/layout/ProtectedRoute'

// Pagine auth
import { LoginPage } from './pages/auth/LoginPage'
import { TwoFactorPage } from './pages/auth/TwoFactorPage'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { SetPasswordPage } from './pages/auth/SetPasswordPage'

// Pagine principali
import { DashboardPage } from './pages/dashboard/DashboardPage'
import { TicketsListPage } from './pages/tickets/TicketsListPage'
import { TicketDetailPage } from './pages/tickets/TicketDetailPage'
import { NewTicketPage } from './pages/tickets/NewTicketPage'
import { EditTicketPage } from './pages/tickets/EditTicketPage'
import { ReportsListPage } from './pages/reports/ReportsListPage'
import { ReportDetailPage } from './pages/reports/ReportDetailPage'
import { ReportPreviewPage } from './pages/reports/ReportPreviewPage'
import { TechniciansPage } from './pages/technicians/TechniciansPage'
import { TechnicianDetailPage } from './pages/technicians/TechnicianDetailPage'
import { TechnicianCalendarPage } from './pages/technicians/TechnicianCalendarPage'
import { CompaniesPage } from './pages/companies/CompaniesPage'
import { CompanyDetailPage } from './pages/companies/CompanyDetailPage'
import { NewCompanyPage } from './pages/companies/NewCompanyPage'
import { CompanyImportPage } from './pages/companies/CompanyImportPage'
import { WarehousePage } from './pages/warehouse/WarehousePage'
import { MaterialsPage } from './pages/warehouse/MaterialsPage'
import { MaterialsImportPage } from './pages/warehouse/MaterialsImportPage'
import { MaterialDetailPage } from './pages/warehouse/MaterialDetailPage'
import { MovementsPage } from './pages/warehouse/MovementsPage'
import { VanStocksPage } from './pages/warehouse/VanStocksPage'
import { InventoryPage } from './pages/warehouse/InventoryPage'
import { LiveMapPage } from './pages/geolocation/LiveMapPage'
import { HSEPage } from './pages/hse/HSEPage'
import { DocumentsPage } from './pages/hse/DocumentsPage'
import { CertificationsPage } from './pages/hse/CertificationsPage'
import { IncidentsPage } from './pages/hse/IncidentsPage'
import { LavoratoriPage } from './pages/hse/LavoratoriPage'
import { LavoratorePage } from './pages/hse/LavoratorePage'
import { AssetsPage } from './pages/maintenance/AssetsPage'
import { ChecklistsPage } from './pages/maintenance/ChecklistsPage'
import { ChecklistWizardPage } from './pages/maintenance/ChecklistWizardPage'
import { ChecklistDetailPage } from './pages/maintenance/ChecklistDetailPage'
import { AnalyticsPage } from './pages/analytics/AnalyticsPage'
import { LeaderboardPage } from './pages/gamification/LeaderboardPage'
import { IntegrationsPage } from './pages/integrations/IntegrationsPage'
import { UsersPage } from './pages/users/UsersPage'
import { SettingsPage } from './pages/settings/SettingsPage'
import { HelpPage } from './pages/help/HelpPage'
import { HelpArticlePage } from './pages/help/HelpArticlePage'
import { HelpSearchPage } from './pages/help/HelpSearchPage'
import { ChatPage } from './pages/chat/ChatPage'
import { BranchesPage } from './pages/my-company/BranchesPage'
import { AttendancePage } from './pages/attendance/AttendancePage'
import { ApprovalsPage } from './pages/attendance/ApprovalsPage'
import { PermessiPage } from './pages/attendance/PermessiPage'
import { LeaveTypesPage } from './pages/settings/LeaveTypesPage'
import { MobilePage } from './pages/mobile/MobilePage'

// Portale clienti
import { PortalLoginPage } from './pages/portal/PortalLoginPage'
import { PortalLayout } from './pages/portal/PortalLayout'
import { PortalDashboardPage } from './pages/portal/PortalDashboardPage'
import { PortalTicketsListPage } from './pages/portal/PortalTicketsListPage'
import { PortalNewTicketPage } from './pages/portal/PortalNewTicketPage'
import { PortalTicketDetailPage } from './pages/portal/PortalTicketDetailPage'
import { PortalReportsPage } from './pages/portal/PortalReportsPage'
import { PortalRatingPage } from './pages/portal/PortalRatingPage'
import { PortalCalendarPage } from './pages/portal/PortalCalendarPage'
import { PublicRequestPage } from './pages/portal/PublicRequestPage'

// Loading fallback
function PageLoading() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-gray-600">Caricamento KleisNote...</p>
      </div>
    </div>
  )
}

export function App() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Routes pubbliche */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/2fa" element={<TwoFactorPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />

        {/* Portale clienti — login e form pubblica senza auth */}
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/richiesta" element={<PublicRequestPage />} />

        {/* Portale clienti — area autenticata */}
        <Route path="/portal" element={<PortalLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<PortalDashboardPage />} />
          <Route path="tickets" element={<PortalTicketsListPage />} />
          <Route path="tickets/new" element={<PortalNewTicketPage />} />
          <Route path="tickets/:id" element={<PortalTicketDetailPage />} />
          <Route path="reports" element={<PortalReportsPage />} />
          <Route path="rating" element={<PortalRatingPage />} />
          <Route path="calendar" element={<PortalCalendarPage />} />
        </Route>

        {/* Anteprima rapportino — protetta ma senza sidebar (full-screen) */}
        <Route
          path="/reports/:id/preview"
          element={
            <ProtectedRoute>
              <ReportPreviewPage />
            </ProtectedRoute>
          }
        />

        {/* Routes protette con layout */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route index element={<DashboardPage />} />

          {/* Ticket */}
          <Route path="tickets">
            <Route index element={<TicketsListPage />} />
            <Route path="new" element={<NewTicketPage />} />
            <Route path=":id" element={<TicketDetailPage />} />
            <Route path=":id/edit" element={<EditTicketPage />} />
          </Route>

          {/* Rapportini */}
          <Route path="reports">
            <Route index element={<ReportsListPage />} />
            <Route path=":id" element={<ReportDetailPage />} />
          </Route>

          {/* Tecnici */}
          <Route path="technicians">
            <Route index element={<TechniciansPage />} />
            <Route path="calendar" element={<TechnicianCalendarPage />} />
            <Route path=":id" element={<TechnicianDetailPage />} />
          </Route>

          {/* Clienti */}
          <Route path="companies">
            <Route index element={<CompaniesPage />} />
            <Route path="new" element={<NewCompanyPage />} />
            <Route path="import" element={<CompanyImportPage />} />
            <Route path=":id" element={<CompanyDetailPage />} />
          </Route>

          {/* Magazzino */}
          <Route path="warehouse">
            <Route index element={<WarehousePage />} />
            <Route path="materials" element={<MaterialsPage />} />
            <Route path="materials/import" element={<MaterialsImportPage />} />
            <Route path="materials/:id" element={<MaterialDetailPage />} />
            <Route path="movements" element={<MovementsPage />} />
            <Route path="van-stocks" element={<VanStocksPage />} />
            <Route path="inventory" element={<InventoryPage />} />
          </Route>

          {/* Geolocalizzazione */}
          <Route path="geolocation" element={<LiveMapPage />} />

          {/* HSE */}
          <Route path="hse">
            <Route index element={<HSEPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="certifications" element={<CertificationsPage />} />
            <Route path="incidents" element={<IncidentsPage />} />
            <Route path="lavoratori" element={<LavoratoriPage />} />
            <Route path="lavoratori/:id" element={<LavoratorePage />} />
          </Route>

          {/* Manutenzione */}
          <Route path="maintenance">
            <Route path="assets" element={<AssetsPage />} />
            <Route path="checklists" element={<ChecklistsPage />} />
            <Route path="checklists/new" element={<ChecklistWizardPage />} />
            <Route path="checklists/:id" element={<ChecklistDetailPage />} />
          </Route>

          {/* Analytics */}
          <Route path="analytics" element={<AnalyticsPage />} />

          {/* Chat interna */}
          <Route path="chat" element={<ChatPage />} />

          {/* Gamification */}
          <Route path="gamification" element={<LeaderboardPage />} />

          {/* Integrazioni */}
          <Route path="integrations" element={<IntegrationsPage />} />

          {/* Presenze & Permessi */}
          <Route path="presenze" element={<AttendancePage />} />
          <Route path="presenze/richieste" element={<ApprovalsPage />} />
          <Route path="permessi" element={<PermessiPage />} />

          {/* Impostazioni */}
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/tipi-permesso" element={<LeaveTypesPage />} />

          {/* Help */}
          <Route path="help">
            <Route index element={<HelpPage />} />
            <Route path="article/:slug" element={<HelpArticlePage />} />
            <Route path="search" element={<HelpSearchPage />} />
          </Route>

          {/* App Mobile — download APK */}
          <Route path="mobile" element={<MobilePage />} />

          {/* La mia azienda */}
          <Route path="my-company">
            <Route index element={<Navigate to="branches" replace />} />
            <Route path="branches" element={<BranchesPage />} />
          </Route>

          {/* Gestione Utenti — solo admin */}
          <Route
            path="admin/users"
            element={
              <ProtectedRoute roles={['admin', 'super_admin']}>
                <UsersPage />
              </ProtectedRoute>
            }
          />

          {/* Redirect catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
